// Extracted from src/pages/staff/VisitorStatistics.js (hourly visitors
// chart) in PR 12 ST 9. Owns selectedDate (local — no other component
// consumes it) and the useHourlyStats query. tz_offset is browser-local
// per the legacy behavior (UTC+8 → -480 in min).

import { useState, type FC } from "react";
import { Calendar } from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { useHourlyStats } from "@shared/api/hooks/useStats";

function localDateString(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatHour(hour: number): string {
  if (hour === 0) return "12 AM";
  if (hour === 12) return "12 PM";
  return hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
}

const HourlyChart: FC = () => {
  const [selectedDate, setSelectedDate] = useState<string>(localDateString());
  const tz_offset = new Date().getTimezoneOffset();
  const { data: hourlyData = [], isLoading } = useHourlyStats({
    date: selectedDate,
    tz_offset,
  });

  const chartData = hourlyData.map((row) => ({
    ...row,
    label: formatHour(row.hour),
  }));

  return (
    <div className="bg-white border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold">DX Today</h2>
          <p className="text-sm text-muted-foreground">
            Hourly unique visitors across the whole DX
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="border border-border rounded-md px-3 py-1.5 text-sm"
            data-testid="hourly-date-picker"
          />
        </div>
      </div>

      <div className="h-80" data-testid="hourly-chart">
        {isLoading ? (
          <div className="h-full flex items-center justify-center text-muted-foreground">
            Loading chart...
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} interval={2} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                }}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ fill: "#3b82f6", strokeWidth: 2 }}
                activeDot={{ r: 6 }}
                name="Unique Visitors"
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default HourlyChart;
